import { Link, router } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup"
import ProductActions from "./components/ProductActions"
import ProductGrid, { type ProductRow } from "./components/ProductGrid"
import { warehouse_path, warehouses_path } from "@/lib/routes"
import { useMarket } from "@/lib/useMarket"

interface Option {
  id: string
  name: string
}

interface Props {
  warehouse: { id: string; external_id: number; name: string; code: string | null }
  channels: Option[]
  selected_node_id: string | null
  rows: ProductRow[]
  can: { manage: boolean }
}

// Warehouse detail: the channels it serves, with the selling/gifting product
// list for one selected channel at a time.
export default function WarehousesShow({
  warehouse,
  channels,
  selected_node_id,
  rows,
  can,
}: Props) {
  const market = useMarket()

  function selectChannel(nodeId: string) {
    router.get(
      warehouse_path(market.slug, warehouse.id),
      { node_id: nodeId },
      { preserveState: false }
    )
  }

  return (
    <AppLayout
      header={
        <div className="flex items-center gap-3">
          <Link
            href={warehouses_path(market.slug)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            <span className="sr-only">Back to Warehouses</span>
          </Link>
          <PageTitle
            title={warehouse.name}
            subtitle={`Which products this warehouse sells or gifts on each channel it serves${
              warehouse.code ? ` · ${warehouse.code}` : ""
            }`}
          />
        </div>
      }
      headerActions={
        can.manage &&
        selected_node_id && (
          <ProductActions
            orgNodeId={selected_node_id}
            warehouseId={warehouse.id}
            existingExternalIds={new Set(rows.map((r) => r.product_external_id))}
          />
        )
      }
    >
      <div className="space-y-4">
        {channels.length === 0 ? (
          <div className="border border-border rounded-lg bg-background p-8 text-center text-sm text-muted-foreground">
            This warehouse serves no sale channel yet. Assign its channels on
            the Warehouses page first.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 items-center">
              <span />
              <div className="flex justify-center">
                <ToggleGroup
                  type="single"
                  value={selected_node_id ?? undefined}
                  onValueChange={(v) => v && selectChannel(v)}
                >
                  {channels.map((node) => (
                    <ToggleGroupItem key={node.id} value={node.id}>
                      {node.name}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              <span className="justify-self-end text-sm text-muted-foreground">
                {rows.length} product{rows.length === 1 ? "" : "s"}
              </span>
            </div>

            {selected_node_id && <ProductGrid rows={rows} canManage={can.manage} />}
          </>
        )}
      </div>
    </AppLayout>
  )
}
